import { Constants, REMOTE_CONFIG_PATH } from "../api-communication/ApiConstants";

import { createAxiosInstance } from "../axios-config/axios_config";
import { isEmpty } from "lodash";
import { rebindOpenSocket } from "../api-communication/WebSocketCli";

type IntegrationConfig = {
  restApiUrl?: string;
  webSocketApiUrl?: string;
};

type RemoteConfig = Record<string, IntegrationConfig>;

const INTEGRATION_LOOKUP_TIMEOUT_MS = 3000;

export function usesOwnBackend(): boolean {
  return (
    process.env.REACT_APP_USE_LOCAL_SERVERS === "true" || process.env.REACT_APP_OFFLINE === "true"
  );
}

export async function getRemoteConfig(): Promise<RemoteConfig> {
  const response = await fetch(REMOTE_CONFIG_PATH);
  return response.json();
}

function applyIntegration(integration?: IntegrationConfig): void {
  if (isEmpty(integration)) return;

  if (!isEmpty(integration?.restApiUrl)) {
    Constants.instance.REST_API_URL = integration.restApiUrl;
  }
  if (!isEmpty(integration?.webSocketApiUrl)) {
    const urlChanged = Constants.instance.WEBSOCKET_API_URL !== integration.webSocketApiUrl;
    Constants.instance.WEBSOCKET_API_URL = integration.webSocketApiUrl;
    if (urlChanged) rebindOpenSocket();
  }

  createAxiosInstance();
}

async function resolveIntegrationKey(
  config: RemoteConfig,
  username?: string | null,
): Promise<string | null> {
  const normalizedUsername = username?.trim();
  if (!normalizedUsername) return null;

  const entryApiUrl = config[process.env.REACT_APP_CONFIG_KEY]?.restApiUrl;
  if (!entryApiUrl) return null;

  try {
    const response = await fetch(
      `${entryApiUrl}/user-integration/${encodeURIComponent(normalizedUsername)}`,
      { signal: AbortSignal.timeout(INTEGRATION_LOOKUP_TIMEOUT_MS) },
    );
    if (!response.ok) return null;

    return (await response.json())?.integrationKey?.trim() || null;
  } catch {
    return null;
  }
}

export async function getUserIntegrationKey(username?: string | null): Promise<string | null> {
  if (usesOwnBackend()) return null;

  try {
    return await resolveIntegrationKey(await getRemoteConfig(), username);
  } catch {
    return null;
  }
}

export default async function setUserIntegration(username): Promise<void> {
  if (usesOwnBackend()) return;

  try {
    const config = await getRemoteConfig();
    const integrationKey =
      (await resolveIntegrationKey(config, username)) ?? process.env.REACT_APP_CONFIG_KEY;
    const integration = config[integrationKey];

    if (isEmpty(integration)) {
      console.warn(`No remote config found for integration key ${integrationKey}`);
      return;
    }

    applyIntegration(integration);
  } catch (err) {
    console.error("Failed to fetch remote config:", err);
  }
}
